import * as React from 'react';
import { AuthenticatedUser } from '../../../../../../../shared/types';
import { IImageThumbnail } from '../../../Entities';
import { IExtraImageCost, UploadableProductData } from '../../listImages/queries/use-query-images';
import { isImageProcessingReady } from './utils';
import { useArchiveCheckQuery } from '../../archiving/queries/use-query-archive-status';
import { useImagesQuery } from '../../listImages/queries/use-query-images';
import { usePhotoStatusQuery } from '../queries/use-query-photo-status';
import { useImageCategoryOverrides } from '../../imageCategoryChange/state/use-image-category-overrides';
import { useProductCategoryChanges } from '../../imageCategoryChange/state/use-product-category-changes';

export enum ImageProcessingStatus {
    Loading = 'loading',
    Archived = 'archived',
    UnderProduction = 'under-production',
    Ready = 'ready'
}

type LoadingState = {
    status: ImageProcessingStatus.Loading;
};

type ArchivedState = {
    status: ImageProcessingStatus.Archived;
    onRetrieve: () => void;
};

type UnderProductionState = {
    status: ImageProcessingStatus.UnderProduction;
};

type ReadyState = {
    status: ImageProcessingStatus.Ready;
    photoStatus: any;
    uploadableProducts: UploadableProductData[];
    allImages: IImageThumbnail[];
    getEffectiveCategory: (image: IImageThumbnail) => number;
    toggleCategory: (imageId: number) => void;
    totalExtraImageCount: number;
    totalExtraImageCost: IExtraImageCost | null;
    totalDeliveredCount: number;
};

export type CustomerImageProcessingDataState = LoadingState | ArchivedState | UnderProductionState | ReadyState;

type UseCustomerImageProcessingDataArgs = {
    orderId: number;
    user: AuthenticatedUser;
};

export function useCustomerImageProcessingData({
    orderId,
    user
}: UseCustomerImageProcessingDataArgs): CustomerImageProcessingDataState {
    let { isPreArchived, isLoading: isArchiveLoading } = useArchiveCheckQuery({ orderId, user });

    let [fetchImagesOverride, setFetchImagesOverride] = React.useState(false);
    let shouldFetchImages = !isArchiveLoading && (!isPreArchived || fetchImagesOverride);

    let photoStatusQuery = usePhotoStatusQuery({ orderId, user, enabled: shouldFetchImages });
    let imagesQuery = useImagesQuery({ orderId, user, enabled: shouldFetchImages });

    let uploadableProducts = imagesQuery.data;

    let allImages = React.useMemo(
        () =>
            uploadableProducts
                ? uploadableProducts.reduce(
                      (acc, product) => acc.concat(product.thumbnails),
                      [] as IImageThumbnail[]
                  )
                : [],
        [uploadableProducts]
    );

    let { getEffectiveCategory, toggleCategory } = useImageCategoryOverrides({ allImages });

    let { totalExtraImageCount, totalExtraImageCost, totalDeliveredCount } =
        useProductCategoryChanges({
            uploadableProducts,
            getEffectiveCategory
        });

    if (isArchiveLoading || (shouldFetchImages && (photoStatusQuery.isLoading || imagesQuery.isLoading))) {
        return { status: ImageProcessingStatus.Loading };
    }

    if (isPreArchived && !fetchImagesOverride) {
        return {
            status: ImageProcessingStatus.Archived,
            onRetrieve: function handleRetrieve() {
                setFetchImagesOverride(true);
            }
        };
    }

    let photoStatus = photoStatusQuery.data;

    if (!isImageProcessingReady(user, photoStatus)) {
        return { status: ImageProcessingStatus.UnderProduction };
    }

    return {
        status: ImageProcessingStatus.Ready,
        photoStatus,
        uploadableProducts,
        allImages,
        getEffectiveCategory,
        toggleCategory,
        totalExtraImageCount,
        totalExtraImageCost,
        totalDeliveredCount
    };
}
